import React from 'react';
import { Users } from 'lucide-react';
import type { AgentOption, FollowupStats, PriorityFilter } from './types';

const PRIORITY_TABS: { key: PriorityFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'urgent', label: 'Urgent' },
  { key: 'high', label: 'High' },
  { key: 'medium', label: 'Medium' },
  { key: 'low', label: 'Low' },
];

function tabCount(stats: FollowupStats | null, key: PriorityFilter): number | undefined {
  if (!stats) return undefined;
  if (key === 'all') return stats.total_pending;
  if (key === 'overdue') return stats.overdue;
  if (key === 'urgent') return stats.urgent_priority;
  if (key === 'high') return stats.high_priority;
  if (key === 'medium') return stats.medium_priority;
  return stats.low_priority;
}

interface PriorityChipsProps {
  filter: PriorityFilter;
  stats: FollowupStats | null;
  myUserId: string | undefined;
  agentFilter: string;
  onFilterChange: (filter: PriorityFilter) => void;
  onMyTasks: () => void;
}

export function PriorityChips({
  filter,
  stats,
  myUserId,
  agentFilter,
  onFilterChange,
  onMyTasks,
}: PriorityChipsProps) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
      {PRIORITY_TABS.map((tab) => {
        const count = tabCount(stats, tab.key);
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onFilterChange(tab.key)}
            className={`px-5 py-3 rounded-xl font-bold text-sm whitespace-nowrap transition-all flex-shrink-0 ${
              filter === tab.key
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-gray-700 border-2 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {tab.label}
            {count !== undefined && ` (${count})`}
          </button>
        );
      })}
      {myUserId && (
        <button
          type="button"
          onClick={onMyTasks}
          className={`px-5 py-3 rounded-xl font-bold text-sm whitespace-nowrap transition-all flex-shrink-0 ${
            agentFilter === myUserId
              ? 'bg-purple-600 text-white shadow-md'
              : 'bg-white text-gray-700 border-2 border-gray-200 hover:bg-gray-50'
          }`}
        >
          My tasks
        </button>
      )}
    </div>
  );
}

interface AgentControlsProps {
  agents: AgentOption[];
  agentFilter: string;
  groupByAgentOn: boolean;
  onAgentChange: (agentId: string) => void;
  onToggleGroup: () => void;
}

export function AgentControls({
  agents,
  agentFilter,
  groupByAgentOn,
  onAgentChange,
  onToggleGroup,
}: AgentControlsProps) {
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <div className="flex-1">
        <label htmlFor="agent-filter" className="sr-only">
          Filter by agent
        </label>
        <select
          id="agent-filter"
          value={agentFilter}
          onChange={(e) => onAgentChange(e.target.value)}
          className={
            'w-full px-4 py-3 rounded-xl border-2 border-gray-200 ' +
            'focus:border-purple-500 focus:outline-none text-sm ' +
            'bg-white font-medium text-gray-700'
          }
        >
          <option value="">All agents</option>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.name}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1 px-1">
          Only agents with pending tasks appear.
        </p>
      </div>
      <button
        type="button"
        onClick={onToggleGroup}
        className={`px-5 py-3 rounded-xl font-bold text-sm whitespace-nowrap transition-all flex items-center justify-center gap-2 flex-shrink-0 ${
          groupByAgentOn
            ? 'bg-purple-600 text-white shadow-md'
            : 'bg-white text-gray-700 border-2 border-gray-200 hover:bg-gray-50'
        }`}
      >
        <Users size={16} />
        Group by agent
      </button>
    </div>
  );
}
